import { GameResult } from '../models/GameResult';
import { GameResultService } from '../services/GameResultService';

type RankingType = 'daily' | 'weekly' | 'allTime';

export class RankingDisplay {
  private container: HTMLElement;
  private gameResultService: GameResultService;
  private currentType: RankingType = 'daily';
  private isLoading: boolean = false;
  
  constructor() {
    this.gameResultService = new GameResultService();
    this.container = document.createElement('div');
    this.container.className = 'ranking-display';
    this.render();
    this.loadRankings();
  }
  
  private render(): void {
    this.container.innerHTML = `
      <div class="ranking-header">
        <h2>🏆 ランキング</h2>
        <button class="close-ranking" title="閉じる">×</button>
      </div>
      
      <div class="ranking-tabs">
        <button class="ranking-tab ${this.currentType === 'daily' ? 'active' : ''}" data-type="daily">
          デイリー
        </button>
        <button class="ranking-tab ${this.currentType === 'weekly' ? 'active' : ''}" data-type="weekly">
          週間
        </button>
        <button class="ranking-tab ${this.currentType === 'allTime' ? 'active' : ''}" data-type="allTime">
          全期間
        </button>
      </div>
      
      <div class="ranking-list">
        <div class="loading">読み込み中...</div>
      </div>
    `;
    
    this.attachEventHandlers();
  }
  
  private attachEventHandlers(): void {
    // タブ切り替え
    const tabs = this.container.querySelectorAll('.ranking-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const type = (e.target as HTMLElement).dataset.type as RankingType;
        if (type && type !== this.currentType) {
          this.currentType = type;
          this.render();
          this.loadRankings();
        }
      });
    });
    
    // 閉じるボタン
    const closeBtn = this.container.querySelector('.close-ranking');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hide();
      });
    }
  }
  
  private async loadRankings(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    
    const listContainer = this.container.querySelector('.ranking-list');
    if (!listContainer) return;
    
    try {
      let rankings: GameResult[] = [];
      
      switch (this.currentType) {
        case 'daily':
          rankings = await this.gameResultService.getDailyRanking(20);
          break;
        case 'weekly':
          rankings = await this.gameResultService.getWeeklyRanking(20);
          break;
        case 'allTime':
          rankings = await this.gameResultService.getAllTimeRanking(20);
          break;
      }
      
      if (rankings.length === 0) {
        listContainer.innerHTML = '<div class="no-results">まだ記録がありません</div>';
      } else {
        listContainer.innerHTML = this.renderRankingList(rankings);
      }
    } catch (error) {
      console.error('Error loading rankings:', error);
      listContainer.innerHTML = '<div class="error">ランキングの読み込みに失敗しました</div>';
    } finally {
      this.isLoading = false;
    }
  }
  
  private renderRankingList(rankings: GameResult[]): string {
    return `
      <div class="ranking-items">
        ${rankings.map((result, index) => this.renderRankingItem(result, index + 1)).join('')}
      </div>
    `;
  }
  
  private renderRankingItem(result: GameResult, rank: number): string {
    const medalEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '';
    const time = this.formatTime(result.completionTime);
    const displayName = result.isAnonymous ? `${result.userDisplayName} (ゲスト)` : result.userDisplayName;
    
    return `
      <div class="ranking-item rank-${rank}">
        <div class="rank-number">
          ${medalEmoji || rank}
        </div>
        <div class="rank-name">
          ${displayName}
        </div>
        <div class="rank-time">
          ${time}
        </div>
      </div>
    `;
  }
  
  private formatTime(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const ms = milliseconds % 1000;
    
    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}.${Math.floor(ms / 100)}`;
    } else {
      return `${seconds}.${Math.floor(ms / 10).toString().padStart(2, '0')}秒`;
    }
  }
  
  public show(): void {
    this.container.style.display = 'block';
    this.loadRankings();
  }
  
  public hide(): void {
    this.container.style.display = 'none';
  }
  
  public mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
    this.container.style.display = 'none'; // 初期状態は非表示
  }
  
  public unmount(): void {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}